/**
 * V3.1m-sprint3 — Surface métier chantier.
 *
 * 4 onglets intégrés dans la fiche chantier :
 *   - Timeline (journal central)
 *   - Avancement (phases + progression)
 *   - Incidents
 *   - Réserves
 *
 * Design : cohérent avec le reste de la fiche chantier (shadcn + Tailwind,
 * cartes blanches sur fond slate-50, Manrope pour les titres).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Activity, ListChecks, AlertTriangle, Flag, CheckCircle2, Circle, X, Plus,
  Clock, UserCircle, Loader2, Trash2, Pencil,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

// Catalogue de phases suggérées (côté frontend uniquement, backend libre)
const PHASE_SUGGESTIONS = [
  'Démolition', 'Maçonnerie', 'Plomberie', 'Électricité',
  'CVC / Chauffage', 'Plâtrerie / Isolation', 'Menuiserie',
  'Carrelage', 'Peinture', 'Sols', 'Cuisine', 'Salle de bain',
  'Finitions', 'Nettoyage / réception',
];

const PHASE_STATUS_LABELS = {
  not_started: 'Non démarré',
  in_progress: 'En cours',
  blocked: 'Bloqué',
  completed: 'Terminé',
};

const PHASE_STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-600 border-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  blocked: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const INCIDENT_SEVERITY_COLORS = {
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  critical: 'bg-rose-50 text-rose-700 border-rose-200',
};


export default function ChantierBusinessPanels({ chantierId }) {
  const [tab, setTab] = useState('timeline');
  return (
    <div
      className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200"
      data-testid="chantier-business-panels"
    >
      <h2
        className="text-base md:text-lg font-semibold text-slate-900 mb-3 md:mb-4"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        Pilotage chantier
      </h2>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList
          className="grid grid-cols-4 w-full md:w-auto bg-slate-100"
          data-testid="chantier-business-tabs"
        >
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <Activity className="w-4 h-4 mr-1.5" /> Timeline
          </TabsTrigger>
          <TabsTrigger value="phases" data-testid="tab-phases">
            <ListChecks className="w-4 h-4 mr-1.5" /> Avancement
          </TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Incidents
          </TabsTrigger>
          <TabsTrigger value="reserves" data-testid="tab-reserves">
            <Flag className="w-4 h-4 mr-1.5" /> Réserves
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="pt-4">
          <TimelinePanel chantierId={chantierId} />
        </TabsContent>
        <TabsContent value="phases" className="pt-4">
          <PhasesPanel chantierId={chantierId} />
        </TabsContent>
        <TabsContent value="incidents" className="pt-4">
          <IncidentsPanel chantierId={chantierId} />
        </TabsContent>
        <TabsContent value="reserves" className="pt-4">
          <ReservesPanel chantierId={chantierId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// =========================================================================
// TIMELINE
// =========================================================================
function TimelinePanel({ chantierId }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (reset = true) => {
    try {
      setLoading(true);
      const url = `/chantiers/${chantierId}/timeline?limit=30`
        + (!reset && cursor ? `&cursor=${encodeURIComponent(cursor)}` : '');
      const r = await api.get(url);
      const batch = r.data?.items || [];
      setItems(reset ? batch : [...items, ...batch]);
      setCursor(r.data?.next_cursor || null);
      setHasMore(Boolean(r.data?.next_cursor));
    } catch (e) {
      toast.error('Erreur chargement timeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [chantierId]);

  return (
    <div data-testid="timeline-panel">
      {loading && items.length === 0 ? (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4">
          Aucun événement enregistré pour ce chantier.
        </p>
      ) : (
        <ol className="space-y-2 relative">
          {items.map((ev, idx) => (
            <li
              key={`${ev.timestamp}-${ev.entity_id}-${idx}`}
              className="flex items-start gap-3 border-b border-slate-100 pb-2 last:border-0"
              data-testid={`timeline-item-${idx}`}
            >
              <div className="w-10 shrink-0 text-xs text-slate-400 pt-0.5 font-mono">
                {formatTimestampShort(ev.timestamp)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                  <span className="font-medium text-slate-700">
                    {ev.actor?.display_name || ev.actor?.email || 'système'}
                  </span>
                  <SourceBadge source={ev.source} />
                  <Badge variant="outline" className="text-[10px]">
                    {ev.event_type}
                  </Badge>
                </div>
                <p className="text-sm text-slate-800 mt-0.5">{ev.summary}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {hasMore && (
        <div className="pt-3 text-center">
          <Button
            variant="outline" size="sm"
            onClick={() => load(false)}
            data-testid="timeline-load-more"
          >
            Charger plus
          </Button>
        </div>
      )}
    </div>
  );
}


function SourceBadge({ source }) {
  const label = source || 'web';
  const cls = {
    web: 'bg-slate-100 text-slate-700',
    whatsapp_ai: 'bg-emerald-100 text-emerald-700',
    automation: 'bg-purple-100 text-purple-700',
    api: 'bg-blue-100 text-blue-700',
    system: 'bg-slate-200 text-slate-700',
  }[label] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`text-[10px] rounded px-1.5 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}


function formatTimestampShort(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}


// =========================================================================
// PHASES / AVANCEMENT
// =========================================================================
function PhasesPanel({ chantierId }) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newProgress, setNewProgress] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/chantiers/${chantierId}/phases`);
      setPhases(r.data?.phases || []);
    } catch {
      toast.error('Erreur chargement phases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chantierId]);

  const addPhase = async () => {
    if (!newLabel.trim()) {
      toast.error('Label requis');
      return;
    }
    try {
      await api.post(`/chantiers/${chantierId}/phases`, {
        label: newLabel.trim(),
        progress_percent: Number(newProgress) || 0,
      });
      toast.success('Phase ajoutée');
      setNewLabel('');
      setNewProgress(0);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur ajout phase');
    }
  };

  const updatePhase = async (phase, patch) => {
    try {
      await api.patch(`/chantiers/${chantierId}/phases/${phase.phase_id}`, patch);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const deletePhase = async (phase) => {
    if (!window.confirm(`Supprimer la phase "${phase.label}" ?`)) return;
    try {
      await api.delete(`/chantiers/${chantierId}/phases/${phase.phase_id}`);
      load();
      toast.success('Phase supprimée');
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const globalProgress = useMemo(() => {
    const active = phases.filter(p => !p.deleted_at);
    if (!active.length) return null;
    const total = active.reduce((s, p) => s + (p.progress_percent || 0), 0);
    return Math.round(total / active.length);
  }, [phases]);

  return (
    <div data-testid="phases-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          {globalProgress !== null && (
            <span>
              Avancement global : <strong>{globalProgress}%</strong>
              <span className="text-xs text-slate-400 ml-1">
                (moyenne des phases actives)
              </span>
            </span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm(v => !v)}
          data-testid="phase-add-toggle"
        >
          <Plus className="w-4 h-4 mr-1" /> Ajouter une phase
        </Button>
      </div>

      {showForm && (
        <div
          className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2"
          data-testid="phase-form"
        >
          <div>
            <Label className="text-xs">Phase</Label>
            <Input
              list="phase-suggestions"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Démolition, Plomberie, etc."
              data-testid="phase-label-input"
            />
            <datalist id="phase-suggestions">
              {PHASE_SUGGESTIONS.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs shrink-0">Progression (%)</Label>
            <Input
              type="number" min={0} max={100}
              value={newProgress}
              onChange={e => setNewProgress(e.target.value)}
              className="w-24"
              data-testid="phase-progress-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm" variant="outline"
              onClick={() => setShowForm(false)}
              data-testid="phase-cancel"
            >
              Annuler
            </Button>
            <Button size="sm" onClick={addPhase} data-testid="phase-save">
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : phases.filter(p => !p.deleted_at).length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4">
          Aucune phase définie. Ajoutez-en pour suivre l'avancement par corps d'état.
        </p>
      ) : (
        <ul className="space-y-2">
          {phases.filter(p => !p.deleted_at).map(phase => (
            <PhaseRow
              key={phase.phase_id}
              phase={phase}
              onUpdate={(patch) => updatePhase(phase, patch)}
              onDelete={() => deletePhase(phase)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}


function PhaseRow({ phase, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [progress, setProgress] = useState(phase.progress_percent);
  const [status, setStatus] = useState(phase.status);

  useEffect(() => {
    setProgress(phase.progress_percent);
    setStatus(phase.status);
  }, [phase.phase_id, phase.progress_percent, phase.status]);

  const save = () => {
    const patch = {};
    if (Number(progress) !== phase.progress_percent) patch.progress_percent = Number(progress);
    if (status !== phase.status) patch.status = status;
    if (Object.keys(patch).length) onUpdate(patch);
    setEditing(false);
  };

  return (
    <li
      className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg"
      data-testid={`phase-row-${phase.phase_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900">{phase.label}</span>
          <Badge
            variant="outline"
            className={`text-[10px] ${PHASE_STATUS_COLORS[phase.status]}`}
          >
            {PHASE_STATUS_LABELS[phase.status] || phase.status}
          </Badge>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${phase.progress_percent}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-slate-600 w-10 text-right">
            {phase.progress_percent}%
          </span>
        </div>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            type="number" min={0} max={100}
            value={progress}
            onChange={e => setProgress(e.target.value)}
            className="w-20"
            data-testid={`phase-progress-edit-${phase.phase_id}`}
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32" data-testid={`phase-status-edit-${phase.phase_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PHASE_STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={save} data-testid={`phase-save-${phase.phase_id}`}>
            OK
          </Button>
        </div>
      ) : (
        <div className="flex gap-1">
          <Button
            size="sm" variant="ghost"
            onClick={() => setEditing(true)}
            data-testid={`phase-edit-${phase.phase_id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={onDelete}
            data-testid={`phase-delete-${phase.phase_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </li>
  );
}


// =========================================================================
// INCIDENTS
// =========================================================================
function IncidentsPanel({ chantierId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', severity: 'medium' });

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/chantiers/${chantierId}/incidents`);
      setItems(r.data?.incidents || []);
    } catch {
      toast.error('Erreur chargement incidents');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chantierId]);

  const create = async () => {
    if (!form.title.trim()) {
      toast.error('Titre requis');
      return;
    }
    try {
      await api.post(`/chantiers/${chantierId}/incidents`, form);
      toast.success('Incident créé');
      setForm({ title: '', description: '', severity: 'medium' });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const resolve = async (inc) => {
    try {
      await api.patch(`/chantiers/${chantierId}/incidents/${inc.incident_id}`,
                       { status: 'resolved' });
      toast.success('Incident résolu');
      load();
    } catch { toast.error('Erreur'); }
  };

  const reopen = async (inc) => {
    try {
      await api.patch(`/chantiers/${chantierId}/incidents/${inc.incident_id}`,
                       { status: 'open' });
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (inc) => {
    if (!window.confirm(`Supprimer l'incident "${inc.title}" ?`)) return;
    try {
      await api.delete(`/chantiers/${chantierId}/incidents/${inc.incident_id}`);
      toast.success('Supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="incidents-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          {items.filter(i => i.status !== 'resolved').length} ouvert(s) /
          {' '}{items.length} au total
        </div>
        <Button
          size="sm" onClick={() => setShowForm(v => !v)}
          data-testid="incident-add-toggle"
        >
          <Plus className="w-4 h-4 mr-1" /> Signaler un incident
        </Button>
      </div>

      {showForm && (
        <div
          className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2"
          data-testid="incident-form"
        >
          <div>
            <Label className="text-xs">Titre</Label>
            <Input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Fuite au plafond, panne électrique…"
              data-testid="incident-title-input"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="incident-description-input"
            />
          </div>
          <div>
            <Label className="text-xs">Gravité</Label>
            <Select
              value={form.severity}
              onValueChange={v => setForm({ ...form, severity: v })}
            >
              <SelectTrigger data-testid="incident-severity-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Faible</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="high">Élevée</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm" variant="outline"
              onClick={() => setShowForm(false)}
              data-testid="incident-cancel"
            >
              Annuler
            </Button>
            <Button size="sm" onClick={create} data-testid="incident-save">
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4">Aucun incident signalé.</p>
      ) : (
        <ul className="space-y-2">
          {items.map(inc => (
            <li
              key={inc.incident_id}
              className="p-3 bg-slate-50 border border-slate-200 rounded-lg"
              data-testid={`incident-row-${inc.incident_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{inc.title}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${INCIDENT_SEVERITY_COLORS[inc.severity]}`}
                    >
                      {inc.severity}
                    </Badge>
                    {inc.status === 'resolved' ? (
                      <Badge variant="outline"
                        className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                        Résolu
                      </Badge>
                    ) : (
                      <Badge variant="outline"
                        className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                        Ouvert
                      </Badge>
                    )}
                  </div>
                  {inc.description && (
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">
                      {inc.description}
                    </p>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    {inc.reported_by || '—'} ·{' '}
                    {formatDate(inc.reported_at)}
                    {inc.resolved_at && (
                      <> · résolu le {formatDate(inc.resolved_at)}</>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {inc.status !== 'resolved' ? (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => resolve(inc)}
                      data-testid={`incident-resolve-${inc.incident_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => reopen(inc)}
                      data-testid={`incident-reopen-${inc.incident_id}`}
                    >
                      <Circle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => remove(inc)}
                    data-testid={`incident-delete-${inc.incident_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// =========================================================================
// RÉSERVES
// =========================================================================
function ReservesPanel({ chantierId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [desc, setDesc] = useState('');
  const [resolveDialog, setResolveDialog] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/chantiers/${chantierId}/reserves`);
      setItems(r.data?.reserves || []);
    } catch { toast.error('Erreur chargement réserves'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chantierId]);

  const create = async () => {
    if (!desc.trim()) {
      toast.error('Description requise');
      return;
    }
    try {
      await api.post(`/chantiers/${chantierId}/reserves`, { description: desc });
      toast.success('Réserve ajoutée');
      setDesc('');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const resolve = async () => {
    if (!resolveDialog) return;
    try {
      await api.patch(
        `/chantiers/${chantierId}/reserves/${resolveDialog.reserve_id}`,
        { status: 'resolved', resolution_note: note || null },
      );
      toast.success('Réserve levée');
      setResolveDialog(null);
      setNote('');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const remove = async (r) => {
    if (!window.confirm('Supprimer cette réserve ?')) return;
    try {
      await api.delete(`/chantiers/${chantierId}/reserves/${r.reserve_id}`);
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="reserves-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          {items.filter(i => i.status === 'open').length} à lever /
          {' '}{items.length} au total
        </div>
        <Button
          size="sm" onClick={() => setShowForm(v => !v)}
          data-testid="reserve-add-toggle"
        >
          <Plus className="w-4 h-4 mr-1" /> Ajouter une réserve
        </Button>
      </div>

      {showForm && (
        <div
          className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2"
          data-testid="reserve-form"
        >
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={desc}
              onChange={e => setDesc(e.target.value)}
              rows={3}
              placeholder="Trace de peinture au plafond, carrelage mal aligné…"
              data-testid="reserve-description-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm" variant="outline"
              onClick={() => setShowForm(false)}
              data-testid="reserve-cancel"
            >
              Annuler
            </Button>
            <Button size="sm" onClick={create} data-testid="reserve-save">
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4">Aucune réserve.</p>
      ) : (
        <ul className="space-y-2">
          {items.map(r => (
            <li
              key={r.reserve_id}
              className="p-3 bg-slate-50 border border-slate-200 rounded-lg"
              data-testid={`reserve-row-${r.reserve_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.status === 'resolved' ? (
                      <Badge variant="outline"
                        className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                        Levée
                      </Badge>
                    ) : (
                      <Badge variant="outline"
                        className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        Ouverte
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">
                    {r.description}
                  </p>
                  {r.resolution_note && (
                    <p className="text-xs text-slate-600 mt-1 italic">
                      Résolution : {r.resolution_note}
                    </p>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    {r.created_by} · {formatDate(r.created_at)}
                    {r.resolved_at && (
                      <> · levée le {formatDate(r.resolved_at)} par {r.resolved_by}</>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {r.status !== 'resolved' && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setResolveDialog(r)}
                      data-testid={`reserve-resolve-${r.reserve_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => remove(r)}
                    data-testid={`reserve-delete-${r.reserve_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!resolveDialog} onOpenChange={() => setResolveDialog(null)}>
        <DialogContent data-testid="reserve-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Lever la réserve</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Note de résolution (optionnel)</Label>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              data-testid="reserve-resolution-note"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResolveDialog(null)}>
                Annuler
              </Button>
              <Button onClick={resolve} data-testid="reserve-confirm-resolve">
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
